import type { PortalHubConnection } from '../realtime/hubFactory';
import { startHubWithRetry } from '../realtime/startHubWithRetry';
import type { CancelBeforeDisposingConnection } from '../realtime/startHubWithRetry';
import type { Incident, IncidentId } from '../realtime/contracts';
import { IncidentStore } from './incidentStore';
import { CallStore } from './callStore';
import { ConnectionStateTracker } from './connectionStateTracker';
import { IncidentStreamSubscriptions } from './incidentStreamSubscriptions';
import type { IncidentCall, IncidentCallUpdate } from './callProjection';

export type PortalClientOptions = {
  hub: PortalHubConnection;
  incidents?: IncidentStore;
  calls?: CallStore;
  onError?: (message: string, error: unknown) => void;
};

export class PortalClient {
  public readonly incidents: IncidentStore;
  public readonly calls: CallStore;
  public readonly connection: ConnectionStateTracker;

  private readonly hub: PortalHubConnection;
  private readonly streams: IncidentStreamSubscriptions;
  private readonly reportError: (message: string, error: unknown) => void;
  private readonly detachHandlers: (() => void)[] = [];
  private readonly cancelStart: CancelBeforeDisposingConnection;

  private readonly incidentsDuringReload = new Set<Incident[]>();
  private readonly updatesDuringCallRead = new Set<{ incidentId: IncidentId; updates: IncidentCallUpdate[] }>();

  private disposed = false;

  public get hubConnection(): PortalHubConnection {
    return this.hub;
  }

  public get watchedIncidents(): readonly IncidentId[] {
    return this.streams.watched;
  }

  public constructor(options: PortalClientOptions) {
    this.hub = options.hub;
    this.incidents = options.incidents ?? new IncidentStore();
    this.calls = options.calls ?? new CallStore();
    this.connection = new ConnectionStateTracker();
    this.reportError = options.onError ?? (() => undefined);

    this.streams = new IncidentStreamSubscriptions(
      (incidentId) => this.hub.invoke('SubscribeToIncident', incidentId),
      (incidentId) => this.hub.invoke('UnsubscribeFromIncident', incidentId),
    );

    this.attachIncidentHandlers();
    this.attachCallHandlers();

    this.detachHandlers.push(
      this.hub.onReconnecting(() => this.connection.markDisconnected()),
      this.hub.onReconnected(() => this.resume()),
    );

    this.cancelStart = startHubWithRetry(this.hub, {
      onConnected: () => this.resume(),
      onStable: () => this.connection.markStable(),
      onDisconnected: () => this.connection.markDisconnected(),
    });
  }

  public async reload(): Promise<void> {
    const arrivedMeanwhile: Incident[] = [];
    this.incidentsDuringReload.add(arrivedMeanwhile);

    try {
      const incidents = await this.hub.invoke('GetLiveIncidents');
      if (!this.disposed) {
        this.incidents.replaceAll(incidents, arrivedMeanwhile);
      }
    } catch (error) {
      this.reportError('Loading the live incidents failed.', error);
      if (!this.disposed) {
        this.incidents.markFailed();
      }
    } finally {
      this.incidentsDuringReload.delete(arrivedMeanwhile);
    }
  }

  public watchIncident(incidentId: IncidentId): Promise<void> {
    return this.streams.watch(incidentId);
  }

  public unwatchIncident(incidentId: IncidentId): void {
    this.streams.release(incidentId)?.catch((error: unknown) => {
      this.reportError(`Unsubscribing from the streams of incident ${incidentId} failed.`, error);
    });
  }

  public async loadIncidentCall(incidentId: IncidentId): Promise<IncidentCall | null> {
    const read = { incidentId, updates: [] as IncidentCallUpdate[] };
    this.updatesDuringCallRead.add(read);

    try {
      const call = await this.hub.invoke('GetIncidentCall', incidentId);
      return this.disposed ? null : this.calls.seedIncidentCall(incidentId, call ?? null, read.updates);
    } finally {
      this.updatesDuringCallRead.delete(read);
    }
  }

  public dispose(): void {
    this.disposed = true;
    this.cancelStart();
    this.detachHandlers.forEach((detach) => detach());
    this.connection.dispose();
    this.streams.clear();
    void this.hub.dispose();
  }

  private resume(): void {
    this.connection.markConnected();
    void this.reload();
    void this.rewatchIncidents();
  }

  private attachIncidentHandlers(): void {
    const upsert = (incident: Incident): void => {
      this.incidentsDuringReload.forEach((buffer) => buffer.push(incident));
      this.incidents.upsert(incident);
    };

    this.detachHandlers.push(
      this.hub.on('IncidentCreated', upsert),
      this.hub.on('IncidentChanged', upsert),
      this.hub.on('IncidentClosed', upsert),
    );
  }

  private attachCallHandlers(): void {
    const applyToIncident = (incidentId: IncidentId, update: IncidentCallUpdate): void => {
      this.updatesDuringCallRead.forEach((read) => {
        if (read.incidentId === incidentId) {
          read.updates.push(update);
        }
      });
      this.calls.applyIncidentCallUpdate(incidentId, update);
    };

    this.detachHandlers.push(
      this.hub.on('CallNotificationCreated', (call) => this.calls.upsertCall(call)),
      this.hub.on('CallNotificationChanged', (call) => this.calls.upsertCall(call)),
      this.hub.on('CallNotificationEnded', (call) => this.calls.upsertCall(call)),
      this.hub.on('IncidentCallStarted', (incidentId, call) => applyToIncident(incidentId, { type: 'call-started', call })),
      this.hub.on('IncidentCallEnded', (incidentId, endTimeUtc) =>
        applyToIncident(incidentId, { type: 'call-ended', endTimeUtc }),
      ),
      this.hub.on('TranscriptInitialized', (incidentId, transcript) =>
        applyToIncident(incidentId, { type: 'transcript-replaced', transcript }),
      ),
      this.hub.on('TranscriptUpdated', (incidentId, transcript) =>
        applyToIncident(incidentId, { type: 'transcript-replaced', transcript }),
      ),
      this.hub.on('TranscriptMessageUpdated', (incidentId, message) =>
        applyToIncident(incidentId, { type: 'transcript-message-updated', message }),
      ),
    );
  }

  private async rewatchIncidents(): Promise<void> {
    for (const incidentId of this.streams.watched) {
      try {
        await this.hub.invoke('SubscribeToIncident', incidentId);
      } catch (error) {
        this.reportError(`Resubscribing to the streams of incident ${incidentId} failed.`, error);
        continue;
      }

      if (this.calls.hasIncidentCall(incidentId)) {
        try {
          await this.loadIncidentCall(incidentId);
        } catch (error) {
          this.reportError(`Rereading the call of incident ${incidentId} failed.`, error);
        }
      }
    }
  }
}
